<script setup>
import { computed, onMounted, ref } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { requiredIf } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  template: {
    type: Object,
    required: true,
  },
  agentBot: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['sendMessage', 'back']);

const { t } = useI18n();

const store = useStore();
const globalConfig = computed(() => store.getters['globalConfig/get']);

const processedParams = ref({});
const cachedRules = ref({});
const headerFile = ref(null);

const templateName = computed(() => {
  return props.template?.name || '';
});

const templateString = computed(() => {
  return props.template?.components?.find(
    component => component.type === 'BODY'
  ).text;
});

const headerParameters = computed(() => {
  let headerTemplate = props.template.components.find(
    component =>
      component.type === 'HEADER' &&
      ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(component.format)
  );
  if (headerTemplate) {
    let parameter = { type: headerTemplate.format.toLowerCase() };
    parameter[parameter.type] = {
      link: decodeURIComponent(headerTemplate.example.headerHandle[0]),
    };
    if (props.template?.media_id)
      parameter[parameter.type].id = props.template.media_id;

    return [parameter];
  }
  return [];
});

const processVariable = str => {
  return str.replace(/{{|}}/g, '');
};

const processedString = computed(() => {
  let tmpString = templateString.value;
  if (Object.keys(cachedRules.value).length > 0) {
    tmpString = tmpString.replace(/{{([^}]+)}}/g, (match, variable) => {
      const variableKey = processVariable(variable);
      return cachedRules.value[variableKey] || `{{${variable}}}`;
    });
  }

  return tmpString.replace(/{{([^}]+)}}/g, (match, variable) => {
    const variableKey = processVariable(variable);
    return processedParams.value[variableKey] || `{{${variable}}}`;
  });
});

const processedStringWithVariableHighlight = computed(() => {
  let tmpString = templateString.value;
  if (Object.keys(cachedRules.value).length > 0) {
    tmpString = tmpString.replace(/{{([^}]+)}}/g, (match, variable) => {
      const variableKey = processVariable(variable);
      return cachedRules.value[variableKey] || `{{${variable}}}`;
    });
  }

  return tmpString.replace(/{{([^}]+)}}/g, (match, variable) => {
    const variableKey = processVariable(variable);
    const value = processedParams.value[variableKey] || `{{${variable}}}`;
    return `<span class="break-all text-n-slate-12">${value}</span>`;
  });
});

const rules = computed(() => {
  const paramRules = {};
  Object.keys(processedParams.value).forEach(key => {
    paramRules[key] = { required: requiredIf(true) };
  });
  return {
    processedParams: paramRules,
  };
});

const variables = computed(() => {
  let tmpString = templateString.value;
  if (Object.keys(cachedRules.value).length > 0) {
    tmpString = tmpString.replace(/{{([^}]+)}}/g, (match, variable) => {
      const variableKey = processVariable(variable);
      return cachedRules.value[variableKey] || `{{${variable}}}`;
    });
  }
  return tmpString.match(/{{([^}]+)}}/g);
});

const v$ = useVuelidate(rules, { processedParams });

const getFieldErrorType = key => {
  if (!v$.value.processedParams[key]?.$error) return 'info';
  return 'error';
};

const generateVariables = () => {
  let botSystemPrompt = '';
  if (props.agentBot && props.agentBot.bot_config?.system_prompt)
    botSystemPrompt = JSON.parse(props.agentBot.bot_config.system_prompt);
  let variableDict =
    botSystemPrompt && botSystemPrompt[props.template.name]
      ? botSystemPrompt[props.template.name]
      : {};

  if ('variable' in variableDict) {
    cachedRules.value = { ...variableDict };
    delete cachedRules.value.variable;
    if ('blob_id' in cachedRules.value) {
      delete cachedRules.value.blob_id;
    }
    variableDict = variableDict.variable;
  }

  let tmpString = templateString.value;
  if (Object.keys(cachedRules.value).length > 0) {
    tmpString = tmpString.replace(/{{([^}]+)}}/g, (match, variable) => {
      const variableKey = processVariable(variable);
      return cachedRules.value[variableKey] || `{{${variable}}}`;
    });
  }
  const matchedVariables = tmpString.match(/{{([^}]+)}}/g);
  if (!matchedVariables) return;

  const variablesTmp = matchedVariables.map(i => processVariable(i));
  processedParams.value = variablesTmp.reduce((acc, variable) => {
    acc[variable] =
      variableDict && variableDict[variable] ? variableDict[variable] : '';
    return acc;
  }, {});
};

const sendMessage = async () => {
  const isValid = await v$.value.$validate();
  if (!isValid) return;

  let tempProcessedParams = {};
  if (Object.keys(cachedRules.value).length > 0) {
    Object.keys(cachedRules.value).forEach(key => {
      tempProcessedParams[key] = cachedRules.value[key]
        .replace(/{{([^}]+)}}/g, (match, variable) => {
          const variableKey = processVariable(variable);
          return processedParams.value[variableKey] || `{{${variable}}}`;
        })
        .replace(/\t/g, '')
        .trim();
    });
  } else {
    Object.keys(processedParams.value).forEach(key => {
      tempProcessedParams[key] = processedParams.value[key]
        .replace(/\t/g, '')
        .trim();
    });
  }

  const payload = {
    message: processedString.value,
    templateParams: {
      name: props.template.name,
      category: props.template.category,
      language: props.template.language,
      header_params: headerParameters.value,
      processed_params: tempProcessedParams,
    },
  };
  if (headerFile.value) {
    payload.files = [
      globalConfig.value.directUploadsEnabled
        ? headerFile.value.blobSignedId
        : headerFile.value.resource.file,
    ];
  }
  emit('sendMessage', payload);
};

onMounted(() => {
  generateVariables();
});

const hasHeaderTemplate = template => {
  return !!template.components.find(
    component =>
      component.type === 'HEADER' &&
      ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(component.format)
  );
};

const fileName = file => {
  return file.filename || file.name;
};
</script>

<template>
  <div
    class="absolute top-full mt-1.5 max-h-[500px] overflow-y-auto left-0 flex flex-col gap-4 px-4 pt-6 pb-5 items-start w-[460px] h-auto bg-n-solid-2 border border-n-strong shadow-sm rounded-lg"
  >
    <span class="text-sm text-n-slate-12">
      {{
        t(
          'COMPOSE_NEW_CONVERSATION.FORM.WHATSAPP_OPTIONS.TEMPLATE_PARSER.TEMPLATE_NAME',
          { templateName: templateName }
        )
      }}
    </span>

    <div v-if="hasHeaderTemplate(template)" class="flex">
      <div
        class="preview-item flex p-1 bg-slate-50 dark:bg-slate-800 rounded-md w-[15rem] mb-1 mr-2"
      >
        <div class="max-w-[4rem] flex-shrink-0 w-6 flex items-center">
          <img
            v-if="template.components[0]['format'] === 'IMAGE'"
            :src="
              headerFile
                ? headerFile.thumb
                  ? headerFile.thumb
                  : headerFile.file_url
                : template.components[0]['example']['headerHandle'][0]
            "
            class="image-thumb"
          />
          <span v-else class="w-6 h-6 text-lg relative -top-px text-left">
            📄
          </span>
        </div>
        <div
          v-if="template.components[0]['format'] === 'IMAGE'"
          class="max-w-[90%] min-w-[50%] overflow-hidden text-ellipsis ml-2"
        >
          <span
            class="h-4 overflow-hidden text-sm font-medium text-ellipsis whitespace-nowrap"
          >
            {{
              headerFile
                ? fileName(
                    headerFile.resource ? headerFile.resource : headerFile
                  )
                : template.components[0]['example']['headerHandle'][0]
                    .split('/')
                    .pop()
            }}
          </span>
        </div>
      </div>
    </div>

    <p
      class="mb-0 text-sm text-n-slate-11"
      v-html="processedStringWithVariableHighlight"
    />

    <span
      v-if="Object.keys(processedParams).length"
      class="text-sm font-medium text-n-slate-12"
    >
      {{
        t(
          'COMPOSE_NEW_CONVERSATION.FORM.WHATSAPP_OPTIONS.TEMPLATE_PARSER.VARIABLES'
        )
      }}
    </span>

    <div v-if="variables">
      <div
        v-for="(variable, key) in processedParams"
        :key="key"
        class="flex items-center w-full gap-2"
      >
        <span
          class="flex items-center h-8 text-sm min-w-6 ltr:text-left rtl:text-right text-n-slate-10"
        >
          {{ key }}
        </span>
        <Input
          v-model="processedParams[key]"
          :message-type="getFieldErrorType(key)"
          class="w-full"
          custom-input-class="!h-8 w-full !bg-transparent"
        />
      </div>
    </div>

    <div class="flex items-end justify-between w-full gap-3 h-14">
      <Button
        :label="
          t(
            'COMPOSE_NEW_CONVERSATION.FORM.WHATSAPP_OPTIONS.TEMPLATE_PARSER.BACK'
          )
        "
        class="w-full font-medium"
        color="slate"
        variant="faded"
        @click="emit('back')"
      />
      <Button
        :label="
          t(
            'COMPOSE_NEW_CONVERSATION.FORM.WHATSAPP_OPTIONS.TEMPLATE_PARSER.SEND_MESSAGE'
          )
        "
        class="w-full font-medium"
        @click="sendMessage"
      />
    </div>
  </div>
</template>
